import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { format } from 'date-fns';

import cn from 'classnames';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  Checkbox,
  DatePicker,
  Input,
  Select,
  SubmitActions,
  TitleSecondary,
} from '../../components';
import LabelElement from '../../components/form/_elements/labelElement/labelElement';

import { config } from '../../api/config';
import { actions } from '../../state';

import { couponSchema } from './coupon.validation';
import { couponDefaultValuesMapper, CouponTypes } from './coupon.data';
import { formatMoneyToCents, mapCouponType } from '../../utils';

import styles from './coupons.module.scss';

const couponTypeOptions = [
  CouponTypes.VIRTUAL,
  CouponTypes.SINGLE_ORDER_DISCOUNT,
  CouponTypes.PERIOD_DISCOUNT,
  CouponTypes.MEMBERSHIP,
].map(type => ({
  name: mapCouponType(type),
  value: type,
}));

const CouponForm = ({
  dispatch,
  coupons: { loading, selectedCoupon },
  memberships: { loading: membershipsLoading, memberships },
  onSubmitCoupon,
  defaultValues,
}) => {
  const isSaved =
    selectedCoupon &&
    defaultValues &&
    defaultValues.valid_from &&
    new Date(defaultValues.valid_from) < new Date();

  const membershipOptions = [
    { name: 'Pasirinkti', value: '' },
    ...memberships.map(m => ({
      name: m.title_lt,
      value: m.id,
    })),
  ];

  const {
    control,
    trigger,
    setValue,
    handleSubmit,
    formState: { isValid, isDirty, errors },
    watch,
  } = useForm({
    mode: 'all',
    resolver: zodResolver(couponSchema),
    defaultValues: couponDefaultValuesMapper(defaultValues),
  });

  const selectedType = watch('type');
  const usageType = watch('usage_type');
  const isNominalOfCode = watch('nominal_of_code');

  const fetchMemberships = useCallback(() => {
    dispatch(
      actions.memberships.getMembershipsAction({
        limit: 1000,
        offset: 0,
        filter: {
          and: [
            {
              field: 'status',
              value: [1],
            },
          ],
        },
        order: [
          {
            by: 'title_lt',
            order: 'asc',
          },
        ],
      })
    );
  }, [dispatch]);

  useEffect(() => {
    fetchMemberships();
  }, [fetchMemberships]);

  const handleSubmitCoupon = useCallback(
    values => {
      const payload = { ...values };

      if (
        payload.type === CouponTypes.VIRTUAL &&
        payload.amount !== undefined &&
        payload.amount !== null &&
        payload.amount !== ''
      ) {
        payload.amount = formatMoneyToCents(payload.amount);
      }

      if (payload.type === CouponTypes.MEMBERSHIP) {
        payload.amount = undefined;
      }

      onSubmitCoupon(payload);
    },
    [onSubmitCoupon]
  );

  return (
    <form onSubmit={handleSubmit(handleSubmitCoupon)} method="POST">
      <div className={styles.formdata}>
        <div className={cn(styles.formdataGroup)}>
          <div
            className={cn(
              styles.formdataGroupContent,
              styles.formdataGroupContentWithWidth
            )}
          >
            <div className={styles.inputsRow}>
              {/* Coupon type */}
              <Controller
                name="type"
                control={control}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <div>
                    <Select
                      name="type"
                      label="Kupono tipas"
                      options={couponTypeOptions}
                      selectedValue={value}
                      disabled={loading || isSaved}
                      onChange={event => {
                        const nextType = Number(event.target.value);
                        onChange(nextType);
                        if (nextType === CouponTypes.MEMBERSHIP) {
                          setValue('amount', undefined, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        }
                        trigger('amount');
                      }}
                      labelContainerClassName={
                        styles.selectLabelContainerClassName
                      }
                    />
                    {error && (
                      <span className={styles.errorMessage}>
                        {error.message}
                      </span>
                    )}
                  </div>
                )}
              />

              {/* Amount */}
              {selectedType !== CouponTypes.MEMBERSHIP && (
                <Controller
                  name="amount"
                  control={control}
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <Input
                      name="amount"
                      label="Suma arba %"
                      type="number"
                      value={value}
                      disabled={loading || isSaved}
                      onChange={event => onChange(Number(event.target.value))}
                      helperText={error?.message}
                    />
                  )}
                />
              )}
            </div>

            {selectedType === CouponTypes.MEMBERSHIP && (
              <div className={styles.inputsRow}>
                {/* Membership plan - only for "Planas" */}
                <Controller
                  name="membership_id"
                  control={control}
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <div>
                      <Select
                        name="membership_id"
                        label="Plano pasirinkimas"
                        options={membershipOptions}
                        selectedValue={value}
                        disabled={loading || membershipsLoading || isSaved}
                        onChange={e => onChange(Number(e.target.value))}
                        labelContainerClassName={
                          styles.selectLabelContainerClassName
                        }
                      />
                      {error && (
                        <span className={styles.errorMessage}>
                          {error.message}
                        </span>
                      )}
                    </div>
                  )}
                />
                {/* Valid days - only for "Planas" */}
                <Controller
                  name="discount_valid_days"
                  control={control}
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <Input
                      name="discount_valid_days"
                      label="Nuo aktyvavimo galioja dienų"
                      type="number"
                      value={value}
                      disabled={loading || isSaved}
                      onChange={e => onChange(Number(e.target.value))}
                      helperText={error?.message}
                    />
                  )}
                />
              </div>
            )}

            {/* Generated coupons count */}
            <Controller
              name="code_amount"
              control={control}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <Input
                  name="code_amount"
                  label="Sugeneruotų kuponų kiekis"
                  type="number"
                  value={value}
                  disabled={loading || isSaved}
                  onChange={e => onChange(Number(e.target.value))}
                  helperText={error?.message}
                  required
                />
              )}
            />

            <div className={styles.inputsRow}>
              {/* Usage type */}
              <Controller
                name="usage_type"
                control={control}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <div>
                    <Select
                      name="usage_type"
                      label="Panaudojimo tipas"
                      options={[
                        { name: 'Vienkartinis', value: 1 },
                        { name: 'Daugkartinis', value: 2 },
                      ]}
                      selectedValue={value}
                      disabled={loading || isSaved}
                      onChange={e => onChange(Number(e.target.value))}
                      labelContainerClassName={
                        styles.selectLabelContainerClassName
                      }
                    />
                    {error && (
                      <span className={styles.errorMessage}>
                        {error.message}
                      </span>
                    )}
                  </div>
                )}
              />

              {/* Max redeem quantity - only for multi-use */}
              {usageType === 2 && (
                <Controller
                  name="max_redeem_quantity"
                  control={control}
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <Input
                      name="max_redeem_quantity"
                      label="Max panaudojimų sk. vienam kuponui"
                      type="number"
                      value={value}
                      disabled={loading || isSaved}
                      onChange={e => onChange(Number(e.target.value))}
                      helperText={error?.message}
                      required={usageType === 2}
                    />
                  )}
                />
              )}
            </div>

            <div className={styles.inputsRow}>
              {/* Validity dates - always editable */}
              <Controller
                name="valid_from"
                control={control}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <div>
                    <div className={styles.selectLabelContainerClassName}>
                      <LabelElement label="Galioja nuo" htmlFor="valid_from" />
                    </div>
                    <DatePicker
                      id="valid_from"
                      name="valid_from"
                      selected={value ? new Date(value) : null}
                      onChange={date =>
                        onChange(date ? format(date, 'yyyy-MM-dd') : '')
                      }
                      popperPlacement="bottom-center"
                      dateFormat={config.DEFAULTS.dateFormat}
                      disabled={loading || isSaved}
                      className={styles.datePicker}
                    />
                    {error?.message && (
                      <span className={styles.errorMessage}>
                        {error.message}
                      </span>
                    )}
                  </div>
                )}
              />
              <Controller
                name="valid_until"
                control={control}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <div>
                    <div className={styles.selectLabelContainerClassName}>
                      <LabelElement label="Galioja iki" htmlFor="valid_until" />
                    </div>
                    <DatePicker
                      id="valid_until"
                      name="valid_until"
                      selected={value ? new Date(value) : null}
                      onChange={date =>
                        onChange(date ? format(date, 'yyyy-MM-dd') : '')
                      }
                      popperPlacement="bottom-center"
                      dateFormat={config.DEFAULTS.dateFormat}
                      disabled={loading || isSaved}
                      className={styles.datePicker}
                    />
                    {error?.message && (
                      <span className={styles.errorMessage}>
                        {error.message}
                      </span>
                    )}
                  </div>
                )}
              />
            </div>

            {/* Unique code toggle */}
            <Controller
              name="nominal_of_code"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Checkbox
                  label="Vardinis kodas"
                  name="nominal_of_code"
                  checked={!!value}
                  value={!!value}
                  onChange={e => onChange(e.target.checked)}
                  disabled={loading || isSaved}
                />
              )}
            />

            {/* Custom code - only when unique code is selected */}
            {isNominalOfCode && (
              <Controller
                name="code"
                control={control}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <Input
                    name="code"
                    label="Kodas"
                    type="text"
                    value={value}
                    disabled={loading || isSaved}
                    onChange={onChange}
                    helperText={error?.message}
                    required={isNominalOfCode}
                  />
                )}
              />
            )}
          </div>
        </div>

        <div className={cn(styles.formdataGroup)}>
          <TitleSecondary>Tekstai atvaizdavimui programėlėje</TitleSecondary>
          <div className={styles.formdataGroupContent}>
            {/* LT */}
            <span className={styles.title_desc_label}>🇱🇹 Lietuvių kalba</span>
            <Controller
              name="title_lt"
              control={control}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <Input
                  name="title_lt"
                  label="Pavadinimas"
                  type="text"
                  value={value}
                  onChange={onChange}
                  helperText={error?.message}
                  required
                />
              )}
            />
            <Controller
              name="description_lt"
              control={control}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <Input
                  name="description_lt"
                  label="Aprašymas"
                  type="textarea"
                  value={value}
                  onChange={e => onChange(e.target.value)}
                  helperText={error?.message}
                />
              )}
            />

            {/* EN */}
            <span className={styles.title_desc_label}>🇬🇧 Anglų kalba</span>
            <Controller
              name="title_en"
              control={control}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <Input
                  name="title_en"
                  label="Pavadinimas"
                  type="text"
                  value={value}
                  onChange={onChange}
                  helperText={error?.message}
                  required
                />
              )}
            />
            <Controller
              name="description_en"
              control={control}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <Input
                  name="description_en"
                  label="Aprašymas"
                  type="textarea"
                  value={value}
                  onChange={e => onChange(e.target.value)}
                  helperText={error?.message}
                />
              )}
            />

            {/* RU */}
            <span className={styles.title_desc_label}>🇷🇺 Rusų kalba</span>
            <Controller
              name="title_ru"
              control={control}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <Input
                  name="title_ru"
                  label="Pavadinimas"
                  type="text"
                  value={value}
                  onChange={onChange}
                  helperText={error?.message}
                  required
                />
              )}
            />
            <Controller
              name="description_ru"
              control={control}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <Input
                  name="description_ru"
                  label="Aprašymas"
                  type="textarea"
                  value={value}
                  onChange={e => onChange(e.target.value)}
                  helperText={error?.message}
                />
              )}
            />
          </div>
        </div>
      </div>

      <SubmitActions
        back={config.APP_PATHS.coupons}
        loading={loading}
        confirm={selectedCoupon ? 'Išsaugoti pakeitimus' : 'Sukurti'}
        disabled={!isDirty || !isValid}
      />
    </form>
  );
};

const mapStateToProps = state => ({
  coupons: state.coupons,
  memberships: state.memberships,
});

CouponForm.propTypes = {
  dispatch: PropTypes.func.isRequired,
  coupons: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    selectedCoupon: PropTypes.object,
  }).isRequired,
  memberships: PropTypes.shape({
    memberships: PropTypes.array.isRequired,
    loading: PropTypes.bool.isRequired,
  }).isRequired,
  onSubmitCoupon: PropTypes.func.isRequired,
  defaultValues: PropTypes.shape({
    type: PropTypes.number,
    amount: PropTypes.number,
    custom_code: PropTypes.bool,
    usage_type: PropTypes.number,
    max_redeem_quantity: PropTypes.number,
    discount_valid_days: PropTypes.number,
    valid_from: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    valid_until: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title_lt: PropTypes.string,
    title_en: PropTypes.string,
    title_ru: PropTypes.string,
    description_lt: PropTypes.string,
    description_en: PropTypes.string,
    description_ru: PropTypes.string,
    code: PropTypes.string,
    code_amount: PropTypes.number,
  }),
};

export default connect(mapStateToProps)(CouponForm);
